import React, { useEffect, useRef, useState } from "react";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	IconButton,
	Box,
	Typography,
	Button,
	DialogActions,
	DialogContentText,
	Radio,
	FormControlLabel,
	RadioGroup,
	FormLabel,
	FormControl,
	Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AbcIcon from "@mui/icons-material/Abc";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import LargeIconButton from "../Inputs/LargeIconButton";
import Grid from "@mui/material/Grid2";
import {
	alphaNumericNoSpacesValidator,
	alphaNumericValidator,
	checkAllValid,
} from "../../Helpers/ValidationHelpers";
import UomAutocomplete from "../Inputs/UomAutocomplete";
import DropdownValuesAutocomplete from "../Inputs/DropdownValuesAutocomplete";
import { produce } from "immer";
import ValidatedTextField from "../Inputs/ValidatedTextField";
import { ulid } from "ulid";
import { GridRowModes } from "@mui/x-data-grid";
import NumbersIcon from "@mui/icons-material/Numbers";
import Divider from "@mui/material/Divider";
import ExistingParametersDataGrid from "../Parameters/ExistingParametersDataGrid";
import { useDispatch } from "react-redux";
import { putProductField } from "../../redux/ProductsSlice";
import TrimObjectStrings from "../../Helpers/TrimObjectStrings";
import { useSnackbar } from "notistack";
import { useContext } from "react";
import { IsOnboardedContext } from "../../Contexts/IsOnboardedContext";
import LoadingOverlay from "../Loading/LoadingOverlay";

const NewParameterDialog = ({
	openDialog,
	handleClose,
	rows,
	setRows,
	setRowModesModel,
	initialState,
	existingFields,
	setRowToEdit,
	isNew,
	setParameterUpdated,
}) => {
	const [step, setStep] = useState("select");
	const [radioGroupValue, setRadioGroupValue] = useState("reuse");
	const [openDuplicateParamDialog, setOpenDuplicateParamDialog] = useState(false);
	const [selectedFieldType, setSelectedFieldType] = useState("");
	const [chosenExistingFields, setChosenExistingFields] = useState(null);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [fieldData, setFieldData] = useState(
		initialState ?? {
			type: 0,
			name: "",
			displayName: "",
			uom: "",
			enumOptions: [],
		}
	);

	const dispatch = useDispatch();
	const { enqueueSnackbar } = useSnackbar();
	const context = useContext(IsOnboardedContext);

	const paramNames = rows.map((x) => x.name);

	const formValid = useRef(null);

	useEffect(() => {
		if (initialState) {
			setFieldData(initialState);
			setStep("details");
			setSelectedFieldType(
				fieldTypes.find(
					(x) =>
						x.enumValue === initialState.type || x.name === initialState.type
				)
			);

			if (initialState.type === 3) {
				formValid.current = {
					name: true,
					displayName: true,
					uom: true,
					enumOptions: true,
				};
			} else {
				formValid.current = {
					name: true,
					displayName: true,
					uom: true,
				};
			}
		} else {
			formValid.current = {
				name: false,
				displayName: false,
				uom: true,
			};
		}
	}, [initialState]);

	const handleChangeDuplicateFieldDecision = (event) => {
		setRadioGroupValue(event.target.value);
	};

	const handleSelectButton = (type) => {
		setSelectedFieldType(type);
		setFieldData((prevState) =>
			produce(prevState, (draft) => {
				draft.type = type.enumValue;
			})
		);
		setStep("details");
		// If options list
		if (type.enumValue === 3) {
			formValid.current.enumOptions = false;
		} else {
			delete formValid.current.enumOptions;
		}
	};

	const handleSelectExistingField = () => {
		setStep("existing");
	};

	const handleConfirmExistingField = () => {
		if (chosenExistingFields && Array.isArray(chosenExistingFields)) {
			setRows((oldRows) =>
				produce(oldRows, (draft) => {
					chosenExistingFields.forEach((id) => {
						const field = existingFields.find((x) => x.id === id);
						if (field) {
							draft.push({
								...field,
								__reorder__: field?.name,
								isNew: true,
							});
						}
					});
				})
			);

			setRowModesModel((oldModel) => {
				const updatedRowModes = {};
				chosenExistingFields.forEach((id) => {
					updatedRowModes[id] = {
						mode: GridRowModes.View,
						fieldToFocus: "name",
					};
				});

				return {
					...oldModel,
					...updatedRowModes,
				};
			});

			handleClose();
			setTimeout(() => {
				handleReset();
			}, 100);
		}
	};

	const handleReset = () => {
		setStep("details");
		setChosenExistingFields(null);
		setFieldData(initialState);
		setSelectedFieldType(
			fieldTypes.find((x) => x.enumValue === initialState?.type)
		);
		formValid.current = {
			name: true,
			displayName: true,
			uom: true,
		};
		if (initialState?.type === 3) {
			formValid.current.enumOptions = true;
		}

		setRowToEdit(null);
	};

	const handleSubmit = (event) => {
		event.preventDefault();
		const id = initialState?.id ?? null;

		if (isNew) {
			if (!initialState && paramNames.includes(fieldData.name)) {
				enqueueSnackbar(`You're already creating a field named '${fieldData.name}'. Please choose a new name.`, {
					variant: "warning",
					autoHideDuration: 3000
				})
				return;
			}

			if (existingFields.map((x) => x.name).includes(fieldData.name)) {
				setOpenDuplicateParamDialog(true);
				return;
			}
		} else {
			const nameChanged = initialState?.name !== fieldData.name;
			const oldNameExists = paramNames.includes(fieldData.name);
			const newNameExists = paramNames.includes(initialState?.name);

			if (nameChanged && oldNameExists && newNameExists) {
				enqueueSnackbar(`You're already creating a field named '${fieldData.name}'. Please choose a new name.`, {
					variant: "warning",
					autoHideDuration: 3000
				})
				return;
			}
		}

		if (isNew) {
			if (initialState) {
				setRows((oldRows) =>
					produce(oldRows, (draft) => {
						const index = draft.findIndex((x) =>
							initialState.id ? x.id === initialState.id : x.name === initialState.name
						);
						 
						if (index !== -1) {
							draft[index] = {
								...fieldData,
								id: initialState.id,
								__reorder__: fieldData?.name,
								isNew: true,
							};
						}
					})
				);

				formValid.current = {
					name: true,
					displayName: true,
					uom: true,
				};
				if (initialState?.type === 3) {
					formValid.current.enumOptions = true;
				}
			} else {
				setRows((oldRows) => [
					...oldRows,
					{
						...fieldData,
						id,
						__reorder__: fieldData?.name,
						isNew: true,
					},
				]);
			}

			setRowModesModel((oldModel) => ({
				...oldModel,
				[id]: { mode: GridRowModes.View, fieldToFocus: "name" },
			}));
			handleClose();
			setTimeout(() => {
				handleReset();
			}, 100);
		} else {
			if (checkAllValid(formValid.current)) {
				setIsSubmitting(true);

				dispatch(
					putProductField(
						TrimObjectStrings({
							id: id,
							payload: {
								...fieldData,
								lastModifiedBy: context.userName,
							},
						})
					)
				)
					.unwrap()
					.then(() => {
						setIsSubmitting(false);

						enqueueSnackbar("Parameter successfully updated!", {
							variant: "success",
							autoHideDuration: 3000,
						});

						setRowModesModel((oldModel) => ({
							...oldModel,
							[id]: { mode: GridRowModes.View, fieldToFocus: "name" },
						}));
						!!setParameterUpdated && setParameterUpdated(true);
						handleClose();
						setTimeout(() => {
							handleReset();
						}, 100);
					})
					.catch((error) => {
						let errorMessage = "Failed to update parameter. Please try again.";
						if (error.status === 409) {
							errorMessage = `Failed to update parameter. ${error.detail}.`;
						}

						console.error("Failed to update parameter", error);
						setIsSubmitting(false);
						enqueueSnackbar(errorMessage, {
							variant: "error",
							autoHideDuration: 3000,
						});

						setRowModesModel((oldModel) => ({
							...oldModel,
							[id]: { mode: GridRowModes.View, fieldToFocus: "name" },
						}));
						handleClose();
						setTimeout(() => {
							handleReset();
						}, 100);
					});
			}
		}
	};

	const handleCloseDialog = () => {
		setFieldData(null);
		handleClose();
		setTimeout(() => {
			handleReset();
		}, 100);
	};

	const handleDuplicateField = () => {
		if (radioGroupValue === "reuse") {
			if (initialState) {
				setRows((oldRows) =>
					produce(oldRows, (draft) => {
						const field = existingFields.find(
							(x) => x.name === fieldData?.name
						);
						const index = draft.findIndex((x) => x.id === initialState.id);
						if (index !== -1) {
							draft[index] = {
								...field,
								id: initialState.id,
								__reorder__: fieldData?.name,
								isNew: true,
							};
						}
					})
				);

				formValid.current = {
					name: true,
					displayName: true,
					uom: true,
				};
				if (initialState?.type === 3) {
					formValid.current.enumOptions = true;
				}

				setRowModesModel((oldModel) => ({
					...oldModel,
					[initialState.id]: { mode: GridRowModes.View, fieldToFocus: "name" },
				}));
			} else {
				const id = ulid();
				setRows((oldRows) =>
					produce(oldRows, (draft) => {
						const field = existingFields.find(
							(x) => x.name === fieldData?.name
						);
						if (field) {
							draft.push({
								...field,
								id,
								__reorder__: fieldData?.name,
								isNew: true,
							});
						}
					})
				);

				setRowModesModel((oldModel) => ({
					...oldModel,
					[id]: { mode: GridRowModes.View, fieldToFocus: "name" },
				}));
			}

			setOpenDuplicateParamDialog(false);
			handleClose();
			setTimeout(() => {
				handleReset();
			}, 100);
		} else if (radioGroupValue === "change") {
			setOpenDuplicateParamDialog(false);
		}
	};

	const handleBack = () => {
		setStep("select");
		setChosenExistingFields(null);
		setFieldData((prevState) =>
			produce(prevState, (draft) => {
				draft.id = initialState?.id ?? null;
				draft.type = 0;
				draft.name = "";
				draft.displayName = "";
				draft.uom = "";
				draft.enumOptions = [];
			})
		);
		setSelectedFieldType("");
		formValid.current = {
			name: false,
			displayName: false,
			uom: true,
		};
	};

	const fieldTypes = [
		{
			name: "Text",
			displayName: "Text",
			description: "Free text, like a name",
			enumValue: 1,
			icon: <AbcIcon fontSize='large' />,
			fields: [
				{
					name: "Name",
					inputComponent: (
						<ValidatedTextField
							id={"name"}
							label={"Name"}
							helperText={"No spaces or punctuation"}
							validator={alphaNumericNoSpacesValidator}
							onChange={(isValid) => (formValid.current.name = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.name}
							fieldPath={["name"]}
							required
							infoAdornment={null}
						/>
					),
				},
				{
					name: "Display Name",
					inputComponent: (
						<ValidatedTextField
							id={"displayName"}
							label={"Display Name"}
							validator={alphaNumericValidator}
							helperText={"The display name seen when quoting"}
							onChange={(isValid) => (formValid.current.displayName = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.displayName}
							fieldPath={["displayName"]}
							required
							infoAdornment={null}
						/>
					),
				},
			],
		},
		{
			name: "Number",
			displayName: "Number",
			description: "A measurement, qty or price",
			enumValue: 2,
			icon: <NumbersIcon fontSize='large' />,
			fields: [
				{
					name: "Name",
					inputComponent: (
						<ValidatedTextField
							id={"name"}
							label={"Name"}
							helperText={"No spaces or punctuation"}
							validator={alphaNumericNoSpacesValidator}
							onChange={(isValid) => (formValid.current.name = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.name}
							fieldPath={["name"]}
							required
							infoAdornment={null}
						/>
					),
				},
				{
					name: "Display Name",
					inputComponent: (
						<ValidatedTextField
							id={"displayName"}
							label={"Display Name"}
							validator={alphaNumericValidator}
							helperText={"The display name seen when quoting"}
							onChange={(isValid) => (formValid.current.displayName = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.displayName}
							fieldPath={["displayName"]}
							required
							infoAdornment={null}
						/>
					),
				},
				{
					name: "Unit of Measure",
					inputComponent: (
						<UomAutocomplete
							id={"uom"}
							formSetter={setFieldData}
							setActiveForm={setFieldData}
							initialValue={fieldData.uom}
						/>
					),
				},
			],
		},
		{
			name: "Boolean",
			displayName: "Toggle",
			description: "Yes or No, True or False",
			enumValue: 4,
			icon: <ToggleOnIcon fontSize='large' />,
			fields: [
				{
					name: "Name",
					inputComponent: (
						<ValidatedTextField
							id={"name"}
							label={"Name"}
							helperText={"No spaces or punctuation"}
							validator={alphaNumericNoSpacesValidator}
							onChange={(isValid) => (formValid.current.name = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.name}
							fieldPath={["name"]}
							infoAdornment={null}
							required
						/>
					),
				},
				{
					name: "Display Name",
					inputComponent: (
						<ValidatedTextField
							id={"displayName"}
							label={"Display Name"}
							validator={alphaNumericValidator}
							helperText={"The display name seen when quoting"}
							onChange={(isValid) => (formValid.current.displayName = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.displayName}
							fieldPath={["displayName"]}
							infoAdornment={null}
							required
						/>
					),
				},
			],
		},
		{
			name: "Enumeration",
			displayName: "Single Select",
			description: "List of options, then pick one",
			enumValue: 3,
			icon: <FormatListBulletedIcon fontSize='large' />,
			fields: [
				{
					name: "Name",
					inputComponent: (
						<ValidatedTextField
							id={"name"}
							label={"Name"}
							helperText={"No spaces or punctuation"}
							validator={alphaNumericNoSpacesValidator}
							onChange={(isValid) => (formValid.current.name = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.name}
							fieldPath={["name"]}
							infoAdornment={null}
							required
						/>
					),
				},
				{
					name: "Display Name",
					inputComponent: (
						<ValidatedTextField
							id={"displayName"}
							label={"Display Name"}
							validator={alphaNumericValidator}
							helperText={"The display name seen when quoting"}
							onChange={(isValid) => (formValid.current.displayName = isValid)}
							setActiveForm={setFieldData}
							initialValue={fieldData.displayName}
							fieldPath={["displayName"]}
							infoAdornment={null}
							required
						/>
					),
				},
				{
					name: "Options",
					inputComponent: (
						<DropdownValuesAutocomplete
							id={"enumOptions"}
							onChange={(isValid) => (formValid.current.enumOptions = isValid)}
							initialValue={fieldData.enumOptions}
							formSetter={setFieldData}
							required
						/>
					),
				},
			],
		},
	];
	const columns = [
		{
			field: "name",
			headerName: "Name",
			width: 160,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "displayName",
			headerName: "Display Name",
			width: 180,
			headerClassName: "new-param-theme--header",
		},
		{
			field: "type",
			headerName: "Type",
			width: 120,
			headerClassName: "new-param-theme--header",
		},
	];

	return (
		<>
			{isSubmitting ? <LoadingOverlay /> : null}
			{fieldData ? (
				<>
					{" "}
					<Dialog
						open={openDialog}
						onClose={handleClose}
					>
						<Box
							sx={{
								backgroundColor: (theme) => theme.palette.primary.main,
								display: "flex",
								justifyContent: "space-between",
								alignItems: "center",
								px: 2,
								py: 1,
							}}
						>
							<Typography
								variant='h6'
								sx={{ color: (theme) => theme.palette.primary.contrastText }}
							>
								{(() => {
									if (step === "select") {
										return isNew ? "New Parameter" : "Edit Parameter";
									} else if (step === "details") {
										if (isNew) {
											return `New Parameter: ${selectedFieldType?.displayName || ""}`;
										} else {
											return `Edit Parameter: ${initialState?.name || ""}`;
										}
									} else if (step === "existing") {
										return "Choose Existing Parameter(s)";
									}
									return "";
								})()}
							</Typography>
							<IconButton
								aria-label='close'
								onClick={handleCloseDialog}
								sx={{
									color: (theme) => theme.palette.primary.contrastText,
								}}
							>
								<CloseIcon />
							</IconButton>
						</Box>
						{step === "select" && (
							// Step 1: Button List
							<>
								<DialogTitle>Select a parameter type</DialogTitle>
								<DialogContent>
									<Grid
										container
										spacing={2}
										sx={{
											mt: 1,
										}}
									>
										{fieldTypes.map((fieldType, index) => (
											<Grid
												size={{ xs: 12, md: 6 }}
												key={`${index}-${fieldType.name}`}
											>
												<LargeIconButton
													icon={fieldType.icon}
													primaryTitle={fieldType.displayName}
													secondaryTitle={fieldType.description}
													variant='outlined'
													onClick={() => handleSelectButton(fieldType)}
												/>
											</Grid>
										))}
									</Grid>
									{ isNew && <>
										<Divider sx={{ m: 1, mx: 0 }}>
											<Chip
												label='OR'
												size={"medium"}
											/>
										</Divider>
										<LargeIconButton
											icon={<MenuOpenIcon fontSize='large' />}
											primaryTitle={"Choose from Existing Parameters"}
											secondaryTitle={
												"Fields that you've already created in the past"
											}
											variant='outlined'
											color='tertiary'
											onClick={handleSelectExistingField}
										/>
									</>
									}
								</DialogContent>
							</>
						)}
						{step === "details" && (
							// Step 2a: Field Details Form (if new field selected)
							<>
								<DialogTitle>
									{`${isNew ? "Add new" : "Edit"} ${
										selectedFieldType?.displayName
									} parameter`}
									<DialogContentText>
										{selectedFieldType?.description}
									</DialogContentText>
								</DialogTitle>
								<DialogContent>
									<Box
										sx={{
											display: "flex",
											flexDirection: "column",
											gap: 2,
											mt: 2,
										}}
									>
										{selectedFieldType?.fields.map((field, index) => {
											return (
												<React.Fragment key={index}>
													{field.inputComponent}
												</React.Fragment>
											);
										})}
									</Box>
								</DialogContent>
								<DialogActions sx={{ mb: 1 }}>
									<Button onClick={handleBack}>Back</Button>
									<Button
										onClick={handleSubmit}
										disabled={!checkAllValid(formValid.current)}
										variant={"contained"}
									>
										Finish
									</Button>
								</DialogActions>
							</>
						)}
						{step === "existing" && (
							// Step 2b: Choose field (if existing field selected)
							<>
								<DialogTitle>Select from existing parameters</DialogTitle>
								<DialogContent>
									<ExistingParametersDataGrid
										fields={existingFields.filter(
											(x) => rows.map((y) => y.name).includes(x.name) === false
										)}
										columns={columns}
										setField={setChosenExistingFields}
									/>
								</DialogContent>
								<DialogActions sx={{ mb: 1 }}>
									<Button onClick={handleBack}>Back</Button>
									<Button
										onClick={handleConfirmExistingField}
										disabled={!chosenExistingFields}
										variant={"contained"}
									>
										Confirm
									</Button>
								</DialogActions>
							</>
						)}
					</Dialog>
					<Dialog open={openDuplicateParamDialog}>
						<DialogTitle>
							You’ve chosen a Parameter Name that already exists
						</DialogTitle>
						<DialogContent>
							<FormControl>
								<FormLabel id='duplicate-field-options-radio-group'>
									Do you want to:
								</FormLabel>
								<RadioGroup
									aria-labelledby='duplicate-field-options-radio-group'
									name='duplicate-fields-radio-buttons-group'
									value={radioGroupValue}
									onChange={handleChangeDuplicateFieldDecision}
								>
									<FormControlLabel
										value='reuse'
										control={<Radio />}
										label='Reuse the existing parameter'
									/>
									<FormControlLabel
										value='change'
										control={<Radio />}
										label='Go back and change the name of this parameter'
									/>
								</RadioGroup>
							</FormControl>
						</DialogContent>
						<DialogActions>
							<Button
								variant={"contained"}
								onClick={handleDuplicateField}
							>
								Continue
							</Button>
						</DialogActions>
					</Dialog>
				</>
			) : null}
		</>
	);
};

export default NewParameterDialog;
